export type AccessLevel = "decideur" | "analyste" | "scientifique";

export type AccessFeature = "evaluation" | "suivi" | "collecte" | "rapport" | "strategie";

const COOKIE_NAME = "nbs_access_level";
const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

const LEVELS: readonly AccessLevel[] = ["decideur", "analyste", "scientifique"];

const FEATURES_BY_LEVEL: Record<AccessLevel, readonly AccessFeature[]> = {
  // Niveau 1 — décideur : parcours d’évaluation uniquement (pas le système de suivi)
  decideur: [],
  // Niveau 2 — analyste : évaluation + suivi, sans rapport
  analyste: ["evaluation", "suivi"],
  // Niveau 3 — scientifique / expert : tout
  scientifique: ["evaluation", "suivi", "collecte", "rapport", "strategie"],
};

export const ACCESS_LEVEL_OPTIONS: readonly {
  readonly id: AccessLevel;
  readonly level: 1 | 2 | 3;
}[] = [
  { id: "decideur", level: 1 },
  { id: "analyste", level: 2 },
  { id: "scientifique", level: 3 },
];

export function isAccessLevel(value: string | null | undefined): value is AccessLevel {
  return value != null && (LEVELS as readonly string[]).includes(value);
}

export function featuresForLevel(level: AccessLevel): readonly AccessFeature[] {
  return FEATURES_BY_LEVEL[level];
}

export function hasFeature(level: AccessLevel | null, feature: AccessFeature): boolean {
  // No profile chosen → full access (modal still shown on each page load).
  if (!level) return true;
  return FEATURES_BY_LEVEL[level].includes(feature);
}

/** Every profile lands on the story entry. */
export function defaultPathForLevel(_level: AccessLevel | null): string {
  return "/";
}

/** Hub sidebar entries allowed for a level. */
export function canAccessHubNavId(level: AccessLevel | null, id: string): boolean {
  if (id === "administration") return false;
  // No profile → full hub access
  if (!level) {
    return (
      id === "accueil" ||
      id === "services" ||
      id === "indicateurs" ||
      id === "collecte" ||
      id === "rapport"
    );
  }
  switch (id) {
    case "accueil":
    case "services":
    case "indicateurs":
      return hasFeature(level, "suivi");
    case "collecte":
      return hasFeature(level, "collecte");
    case "rapport":
      return hasFeature(level, "rapport");
    default:
      return false;
  }
}

/** Main top-nav / route path access. */
export function canAccessPath(level: AccessLevel | null, pathname: string): boolean {
  // No profile → do not gate routes
  if (!level) return true;
  const path = pathname.split("?")[0] ?? pathname;

  // Story entry: the common landing page, open to every profile.
  if (path === "/" || path === "") return true;
  if (path === "/hub" || path.startsWith("/hub/")) {
    if (path.startsWith("/hub/collecte")) return hasFeature(level, "collecte");
    if (path.startsWith("/hub/rapport")) return hasFeature(level, "rapport");
    return hasFeature(level, "suivi");
  }
  if (path === "/n1" || path.startsWith("/n1")) return hasFeature(level, "strategie");
  if (path === "/source-report") return hasFeature(level, "rapport");
  // Legacy AppShell consultation routes share the evaluation surface
  if (
    path === "/dashboard" ||
    path.startsWith("/indicators") ||
    path.startsWith("/measures") ||
    path.startsWith("/n2") ||
    path.startsWith("/n3")
  ) {
    return hasFeature(level, "evaluation");
  }
  // Auth / utility / legal pages stay reachable (ProtectedRoute still applies)
  if (
    path === "/login" ||
    path === "/settings" ||
    path === "/unauthorized" ||
    path === "/forbidden" ||
    path.startsWith("/review") ||
    path.startsWith("/politique-de-confidentialite")
  ) {
    return true;
  }
  return false;
}

function cookieSecureFlag(): string {
  return typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
}

function readAccessLevelStorage(): AccessLevel | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(COOKIE_NAME);
    return isAccessLevel(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeAccessLevelStorage(level: AccessLevel | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (level) localStorage.setItem(COOKIE_NAME, level);
    else localStorage.removeItem(COOKIE_NAME);
  } catch {
    // Private mode / blocked storage — cookie remains the source of truth.
  }
}

export function readAccessLevelCookie(): AccessLevel | null {
  if (typeof document === "undefined") return readAccessLevelStorage();
  const parts = document.cookie.split(";").map((p) => p.trim());
  for (const part of parts) {
    if (!part.startsWith(`${COOKIE_NAME}=`)) continue;
    const raw = decodeURIComponent(part.slice(COOKIE_NAME.length + 1));
    if (isAccessLevel(raw)) {
      writeAccessLevelStorage(raw);
      return raw;
    }
  }
  const stored = readAccessLevelStorage();
  if (stored) writeAccessLevelCookie(stored);
  return stored;
}

export function writeAccessLevelCookie(level: AccessLevel): void {
  if (typeof document !== "undefined") {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(level)}; path=/; max-age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax${cookieSecureFlag()}`;
  }
  writeAccessLevelStorage(level);
}

export function clearAccessLevelCookie(): void {
  if (typeof document !== "undefined") {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax${cookieSecureFlag()}`;
  }
  writeAccessLevelStorage(null);
}
